import { useEffect, useState, type ReactNode } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { cn } from "@codestack/ui";
import { useAuth } from "../lib/auth";
import { initials } from "../lib/format";
import { NotificationBell } from "./NotificationBell";

type NavItem = {
  label: string;
  to: string;
  permission?: string;
  icon: ReactNode;
};

const icon = (path: string) => (
  <svg viewBox="0 0 24 24" fill="none" className="h-[18px] w-[18px]" aria-hidden="true">
    <path
      d={path}
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const navGroups: Array<{ title: string; items: NavItem[] }> = [
  {
    title: "Overview",
    items: [
      {
        label: "Dashboard",
        to: "/",
        icon: icon("M4 13h6V4H4v9Zm0 7h6v-4H4v4Zm10 0h6v-9h-6v9Zm0-16v4h6V4h-6Z"),
      },
      {
        label: "Transactions",
        to: "/transactions",
        permission: "transaction:read",
        icon: icon("M4 7h16M4 12h16M4 17h10"),
      },
      {
        label: "Platforms",
        to: "/platforms",
        permission: "platform:read",
        icon: icon("M4 6h16v5H4zM4 14h7v5H4zM14 14h6v5h-6z"),
      },
      {
        label: "Revenue",
        to: "/revenue",
        permission: "report:read",
        icon: icon("M4 19V9m5 10V5m5 14v-7m5 7V8"),
      },
    ],
  },
  {
    title: "Operations",
    items: [
      {
        label: "Reconciliation",
        to: "/reconciliation",
        permission: "reconciliation:run",
        icon: icon("M4 6h7v12H4zM13 6h7v7h-7zM13 16h7M16.5 13v6"),
      },
      {
        label: "Audit log",
        to: "/audit",
        permission: "audit:read",
        icon: icon("M7 4h10a2 2 0 0 1 2 2v14l-3-2-2 2-2-2-2 2-3-2V6a2 2 0 0 1 2-2Z"),
      },
      {
        label: "System health",
        to: "/health",
        icon: icon("M3 12h4l2-6 4 12 2-6h6"),
      },
    ],
  },
  {
    title: "Content",
    items: [
      {
        label: "Updates",
        to: "/updates",
        permission: "content:manage",
        icon: icon("M5 4h14v16H5zM8 8h8M8 12h8M8 16h5"),
      },
    ],
  },
  {
    title: "Administration",
    items: [
      {
        label: "Team",
        to: "/team",
        permission: "user:manage",
        icon: icon("M12 12a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm-7 8a7 7 0 0 1 14 0"),
      },
      {
        label: "Roles",
        to: "/roles",
        permission: "role:manage",
        icon: icon("M12 3.5 5 6.5v5c0 4.2 2.9 7.6 7 9 4.1-1.4 7-4.8 7-9v-5l-7-3Z"),
      },
      {
        label: "Settings",
        to: "/settings",
        permission: "settings:manage",
        icon: icon(
          "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm8-3a8 8 0 0 0-.1-1.2l2-1.5-2-3.4-2.3 1a8 8 0 0 0-2-1.2L15.2 3h-4l-.4 2.7a8 8 0 0 0-2 1.2l-2.3-1-2 3.4 2 1.5A8 8 0 0 0 6.4 12c0 .4 0 .8.1 1.2l-2 1.5 2 3.4 2.3-1a8 8 0 0 0 2 1.2l.4 2.7h4l.4-2.7a8 8 0 0 0 2-1.2l2.3 1 2-3.4-2-1.5c.1-.4.1-.8.1-1.2Z",
        ),
      },
    ],
  },
];

export function AdminLayout() {
  const { user, logout, can } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const visibleGroups = navGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => !item.permission || can(item.permission)),
    }))
    .filter((group) => group.items.length > 0);

  const sidebar = (
    <div className="flex h-full flex-col">
      <div className="flex h-16 items-center gap-3 border-b border-white/[0.07] px-5">
        {/* The supplied lockup already contains the wordmark, so the separate
            "CodeStack" text is gone. `logo-light` is the light-ink artwork,
            which is what a dark sidebar needs. */}
        <img
          src="/brand/logo-light.png"
          alt="CodeStack Technologies"
          width={93}
          height={32}
          style={{ height: 26, width: "auto" }}
          className="block shrink-0 select-none"
          draggable={false}
        />
        <p className="text-[10px] uppercase tracking-[0.16em] text-ink-500">
          Admin console
        </p>
      </div>

      <nav className="flex-1 space-y-6 overflow-y-auto px-3 py-5" aria-label="Admin">
        {visibleGroups.map((group) => (
          <div key={group.title}>
            <p className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-ink-600">
              {group.title}
            </p>
            <ul className="space-y-0.5">
              {group.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === "/"}
                    className={({ isActive }) =>
                      cn("nav-item", isActive && "nav-item-active")
                    }
                  >
                    {item.icon}
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className="border-t border-white/[0.07] p-3">
        <div className="flex items-center gap-3 rounded-lg px-2 py-2">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brand-500/15 text-[11px] font-bold text-brand-200">
            {initials(user?.name ?? "?")}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-semibold text-white">{user?.name}</p>
            <p className="truncate text-[11px] text-ink-500">
              {user?.roles.join(", ") || "No role"}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => void logout()}
          className="mt-1 w-full rounded-lg px-3 py-2 text-left text-xs font-medium text-ink-400 transition-colors hover:bg-white/[0.05] hover:text-white"
        >
          Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-ink-950">
      <aside className="hidden w-60 shrink-0 border-r border-white/[0.07] lg:block">
        <div className="sticky top-0 h-screen">{sidebar}</div>
      </aside>

      {mobileOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            aria-label="Close navigation"
            className="absolute inset-0 bg-ink-950/80 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="relative h-full w-64 border-r border-white/[0.07] bg-ink-950">
            {sidebar}
          </aside>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-40 flex h-16 items-center gap-3 border-b border-white/[0.07] bg-ink-950/90 px-4 backdrop-blur-xl sm:px-6">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation"
            className="grid h-9 w-9 place-items-center rounded-lg border border-white/10 text-ink-300 lg:hidden"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none">
              <path
                d="M4 7h16M4 12h16M4 17h16"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>

          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-white">
              {pageTitle(location.pathname)}
            </p>
          </div>

          <NotificationBell />

          <Link
            to="/settings"
            className="hidden rounded-lg border border-white/10 px-3 py-1.5 text-xs font-medium text-ink-300 transition-colors hover:text-white sm:block"
          >
            Settings
          </Link>
        </header>

        <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <div className="mx-auto w-full max-w-[1400px] animate-in">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

function pageTitle(pathname: string): string {
  if (pathname === "/") return "Dashboard";
  if (pathname.startsWith("/transactions/")) return "Transaction detail";
  if (pathname.startsWith("/platforms/")) return "Platform detail";
  const segment = pathname.split("/")[1] ?? "";
  return segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, " ");
}
